import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, ArrowLeft, Lock, Eye, EyeOff, Zap, Copy, RefreshCw } from 'lucide-react';

// ─── Constants ─────────────────────────────────────────────────────────────
const BLOCKED_DOMAINS = new Set([
  'gmail.com','yahoo.com','yahoo.fr','hotmail.com','hotmail.fr',
  'outlook.com','outlook.fr','icloud.com','aol.com','protonmail.com',
  'mail.com','gmx.com','yandex.com','live.com','msn.com',
  'zoho.com','tutanota.com','fastmail.com','me.com','pm.me',
  'tempmail.com','guerrillamail.com','mailinator.com','yopmail.com'
]);

const SECURITY_THEATER_STEPS = [
  'Initiation du tunnel sécurisé SSL/TLS...',
  'Génération des clés de session AES-256...',
  'Chiffrement militaire de vos identifiants...',
  'Provisionnement de votre coffre-fort...',
];

// ─── Password utilities ─────────────────────────────────────────────────────
const CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=[]{}|';

function generateSecurePassword(length = 16) {
  const array = new Uint32Array(length);
  window.crypto.getRandomValues(array);
  let pwd = '';
  // Guarantee at least one of each required class
  const upper  = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower  = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const syms   = '!@#$%^&*()_+-=[]{}|';
  const pick = (set) => set[Math.floor(Math.random() * set.length)];
  pwd = pick(upper) + pick(lower) + pick(digits) + pick(syms);
  for (let i = 4; i < length; i++) {
    pwd += CHARSET[array[i] % CHARSET.length];
  }
  // Shuffle deterministically using the crypto array
  return pwd.split('').sort(() => (array[0] % 2 === 0 ? 1 : -1)).join('');
}

function getPasswordStrength(pwd, language = 'en') {
  if (!pwd) return { level: '', color: '', pct: 0, missing: [] };

  const checks = {
    hasLength:  pwd.length >= 12,
    hasUpper:   /[A-Z]/.test(pwd),
    hasLower:   /[a-z]/.test(pwd),
    hasDigit:   /[0-9]/.test(pwd),
    hasSymbol:  /[^A-Za-z0-9]/.test(pwd),
  };

  // Collect exactly what's missing for dynamic feedback
  const missing = [];
  const isFr = language === 'fr';
  if (!checks.hasLength) {
    const needed = 12 - pwd.length;
    missing.push(isFr
      ? `${needed} caractère${needed > 1 ? 's' : ''} min.`
      : `${needed} more char${needed > 1 ? 's' : ''}`);
  }
  if (!checks.hasUpper)  missing.push(isFr ? '1 majuscule'  : '1 uppercase');
  if (!checks.hasLower)  missing.push(isFr ? '1 minuscule'  : '1 lowercase');
  if (!checks.hasDigit)  missing.push(isFr ? '1 chiffre'    : '1 number');
  if (!checks.hasSymbol) missing.push(isFr ? '1 symbole'    : '1 symbol');

  const allMet = missing.length === 0;
  if (allMet) return { level: 'strong', color: 'bg-green-500',  pct: 100, missing: [] };

  const score = Object.values(checks).filter(Boolean).length;
  if (score <= 2) return { level: 'weak',   color: 'bg-red-500',    pct: 33,  missing };
  return           { level: 'medium',  color: 'bg-yellow-500', pct: 66,  missing };
}

function isPasswordStrong(pwd) {
  return (
    pwd.length >= 12 &&
    /[A-Z]/.test(pwd) &&
    /[a-z]/.test(pwd) &&
    /[0-9]/.test(pwd) &&
    /[^A-Za-z0-9]/.test(pwd)
  );
}

// ─── Component ─────────────────────────────────────────────────────────────
export default function Login() {
  const [searchParams] = useSearchParams();
  const [isRegister, setIsRegister]       = useState(() => searchParams.get('mode') === 'register');
  const [email, setEmail]                 = useState('');
  const [password, setPassword]           = useState('');
  const [confirmPwd, setConfirmPwd]       = useState('');
  const [firstName, setFirstName]         = useState('');
  const [lastName, setLastName]           = useState('');
  const [acceptTerms, setAcceptTerms]     = useState(false);
  const [showPwd, setShowPwd]             = useState(false);
  const [showConfirm, setShowConfirm]     = useState(false);
  const [error, setError]                 = useState('');
  const [successMsg, setSuccessMsg]       = useState('');
  const [genToast, setGenToast]           = useState('');
  const [loading, setLoading]             = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [showVerifyPrompt, setShowVerifyPrompt] = useState(false);
  const [theaterStep, setTheaterStep]     = useState(0);
  const theaterRef                        = useRef(null);

  const { login, register } = useAuth();
  const navigate = useNavigate();
  const { t, language } = useTranslation();

  useEffect(() => {
    if (searchParams.get('mode') === 'register') setIsRegister(true);
  }, [searchParams]);

  // Security theater — cycles through phrases while loading (register only)
  useEffect(() => {
    if (loading && isRegister) {
      setTheaterStep(0);
      theaterRef.current = setInterval(() => {
        setTheaterStep(s => (s + 1) % SECURITY_THEATER_STEPS.length);
      }, 1500);
    } else {
      clearInterval(theaterRef.current);
    }
    return () => clearInterval(theaterRef.current);
  }, [loading, isRegister]);

  const strength   = getPasswordStrength(password, language);
  const pwdStrong  = isPasswordStrong(password);
  const pwdMatch   = password === confirmPwd;

  // Bug 1 fix: separate disabled logic for login vs register.
  // Login only blocks on loading. Register requires all fields + strong password + matching confirm.
  const loginDisabled    = loading;
  const registerDisabled = loading || !email || !firstName || !lastName || !acceptTerms || !pwdStrong || !pwdMatch;

  // ─── Handlers ─────────────────────────────────────────────────────────
  const handleResendVerification = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    try {
      await authAPI.resendVerification(email, language);
      setResendSuccess(true);
    } catch { /* silently ignore — API always returns OK */ }
    finally { setResendLoading(false); }
  };

  const handleGeneratePassword = () => {
    const pwd = generateSecurePassword(16);
    setPassword(pwd);
    setConfirmPwd(pwd);
    navigator.clipboard.writeText(pwd).catch(() => {});
    setGenToast(t('auth.generate_toast'));
    setTimeout(() => setGenToast(''), 6000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    setShowVerifyPrompt(false);
    setResendSuccess(false);

    const domain = email.split('@')[1]?.toLowerCase();
    if (domain && BLOCKED_DOMAINS.has(domain)) {
      setError(t('b2b.error'));
      return;
    }

    if (isRegister && !pwdStrong) {
      setError(t('auth.pwd_strong_required'));
      return;
    }

    if (isRegister && !pwdMatch) {
      setError(t('auth.pwd_mismatch_error'));
      return;
    }

    setLoading(true);
    try {
      if (isRegister) {
        const userData = await register(email, password, firstName, lastName, language);
        if (userData?.is_verified === false) {
          setFirstName('');
          setLastName('');
          setEmail('');
          setPassword('');
          setConfirmPwd('');
          setSuccessMsg(t('auth.register_success'));
          return;
        }
        navigate('/dashboard');
      } else {
        const userData = await login(email, password);
        if (userData?.is_verified === false) {
          setError(t('auth.verify_required'));
          setShowVerifyPrompt(true); // show "Resend verification email" button
          return;
        }
        navigate('/dashboard');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.toLowerCase().includes('already registered') || detail.toLowerCase().includes('already exists')) {
        setError(t('auth.email_exists'));
      } else {
        // auth.error_generic is now guaranteed in i18n — no hardcoded fallback needed
        setError(detail || t('auth.error_generic'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMode = () => {
    setIsRegister(!isRegister);
    setError('');
    setSuccessMsg('');
    setGenToast('');
    setPassword('');
    setConfirmPwd('');
    setAcceptTerms(false);
    setShowVerifyPrompt(false);
    setResendSuccess(false);
  };

  // ─── Render ───────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-white dark:bg-[#050505] flex transition-colors" data-testid="login-page">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 border-r border-slate-200 dark:border-white/10">
        <Link to="/" className="flex items-center gap-2 text-slate-900 dark:text-white" data-testid="login-home-link">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          <span className="text-sm font-body tracking-wide">Back</span>
        </Link>
        <div>
          <Shield className="w-10 h-10 text-blue-600 mb-6" strokeWidth={1.5} />
          <h1 className="font-heading text-4xl text-slate-900 dark:text-white font-light leading-tight mb-4">
            {t('auth.left_panel_title')}
          </h1>
          <p className="text-slate-600 dark:text-neutral-500 font-body text-lg max-w-md">
            {t('auth.left_panel_desc')}
          </p>
        </div>
        <p className="text-slate-400 dark:text-neutral-700 text-xs font-body">SheetArmor</p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          {/* Mobile back */}
          <div className="lg:hidden mb-8">
            <Link to="/" className="flex items-center gap-2 text-slate-600 dark:text-neutral-400 mb-6">
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
              <span className="text-sm">Back</span>
            </Link>
            <Shield className="w-8 h-8 text-blue-600 mb-4" strokeWidth={1.5} />
          </div>

          <h2 className="font-heading text-2xl text-slate-900 dark:text-white mb-1">
            {isRegister ? t('auth.register') : t('auth.login')}
          </h2>
          <p className="text-slate-600 dark:text-neutral-500 text-sm font-body mb-6">
            {isRegister ? t('auth.register_subtitle') : 'Welcome back'}
          </p>

          {/* B2B notice */}
          <div className="flex items-center gap-2 p-2.5 bg-blue-600/5 border border-blue-600/20 rounded-sm mb-6">
            <Lock className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" strokeWidth={1.5} />
            <span className="text-blue-400 text-[11px] font-body">{t('pricing.corporate')}</span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* First name — register only */}
            {isRegister && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600 dark:text-neutral-400 text-xs font-body uppercase tracking-wider">
                    {t('auth.first_name')}
                  </Label>
                  <Input
                    value={firstName} onChange={(e) => setFirstName(e.target.value)}
                    className="mt-1 bg-transparent border-slate-300 dark:border-white/20 text-slate-900 dark:text-white focus:ring-blue-600 focus:border-blue-600 h-10 font-body"
                    placeholder="John" required
                    data-testid="register-first-name-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-600 dark:text-neutral-400 text-xs font-body uppercase tracking-wider">
                    {t('auth.last_name')}
                  </Label>
                  <Input
                    value={lastName} onChange={(e) => setLastName(e.target.value)}
                    className="mt-1 bg-transparent border-slate-300 dark:border-white/20 text-slate-900 dark:text-white focus:ring-blue-600 focus:border-blue-600 h-10 font-body"
                    placeholder="Doe" required
                    data-testid="register-last-name-input"
                  />
                </div>
              </div>
            )}

            {/* Email */}
            <div>
              <Label className="text-slate-600 dark:text-neutral-400 text-xs font-body uppercase tracking-wider">
                {t('auth.email')}
              </Label>
              <Input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 bg-transparent border-slate-300 dark:border-white/20 text-slate-900 dark:text-white focus:ring-blue-600 focus:border-blue-600 h-10 font-body"
                placeholder="you@company.com" required
                data-testid="email-input"
              />
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-slate-600 dark:text-neutral-400 text-xs font-body uppercase tracking-wider">
                  {t('auth.password')}
                </Label>
                {!isRegister && (
                  <Link to="/forgot-password" className="text-xs text-blue-600 hover:text-blue-500 dark:hover:text-blue-400 font-body">
                    Forgot password?
                  </Link>
                )}
              </div>

              <div className="relative flex items-center gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showPwd ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-transparent border-slate-300 dark:border-white/20 text-slate-900 dark:text-white focus:ring-blue-600 focus:border-blue-600 h-10 font-body pr-12"
                    placeholder={isRegister ? 'Min. 12 caractères' : 'Min. 6 characters'}
                    required
                    minLength={isRegister ? 12 : 6}
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(v => !v)}
                    className="absolute inset-y-0 right-0 flex items-center justify-center w-10 p-2 z-10 text-slate-400 hover:text-slate-600 dark:hover:text-neutral-300 focus:outline-none"
                    tabIndex={-1}
                    aria-label={showPwd ? 'Hide password' : 'Show password'}
                  >
                    {showPwd
                      ? <EyeOff className="w-4 h-4" strokeWidth={1.5} />
                      : <Eye className="w-4 h-4" strokeWidth={1.5} />}
                  </button>
                </div>

                {/* Generate password button — register only */}
                {isRegister && (
                  <button
                    type="button"
                    onClick={handleGeneratePassword}
                    className="flex-shrink-0 flex items-center gap-1.5 px-3 h-10 text-[11px] font-body tracking-wider text-blue-400 border border-blue-600/30 rounded-sm hover:bg-blue-600/10 transition-colors whitespace-nowrap"
                    title={t('auth.generate_password')}
                  >
                    <Zap className="w-3 h-3" strokeWidth={2} />
                    {t('auth.generate_password')}
                  </button>
                )}
              </div>

              {/* Password strength meter — register only */}
              {isRegister && password.length > 0 && (
                <div className="mt-2 space-y-1">
                  <div className="h-1 w-full bg-slate-200 dark:bg-white/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${strength.color}`}
                      style={{ width: `${strength.pct}%` }}
                    />
                  </div>
                  {strength.level === 'strong' ? (
                    <p className="text-[11px] font-body text-green-500">{t('auth.pwd_strength_strong')}</p>
                  ) : (
                    <p className={`text-[11px] font-body ${strength.level === 'medium' ? 'text-yellow-500' : 'text-red-500'}`}>
                      {strength.level === 'medium' ? t('auth.pwd_strength_medium') : t('auth.pwd_strength_weak')}
                      {strength.missing.length > 0 && (
                        <span className="ml-1">
                          {language === 'fr' ? '— Il manque : ' : '— Missing: '}
                          {strength.missing.join(', ')}
                        </span>
                      )}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Confirm password — register only */}
            {isRegister && (
              <div>
                <Label className="text-slate-600 dark:text-neutral-400 text-xs font-body uppercase tracking-wider">
                  {t('auth.confirm_password')}
                </Label>
                <div className="relative mt-1">
                  <Input
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPwd}
                    onChange={(e) => setConfirmPwd(e.target.value)}
                    className={`bg-transparent h-10 font-body pr-12 ${
                      confirmPwd.length > 0 && !pwdMatch
                        ? 'border-red-500 focus:ring-red-500 text-slate-900 dark:text-white'
                        : 'border-slate-300 dark:border-white/20 text-slate-900 dark:text-white focus:ring-blue-600 focus:border-blue-600'
                    }`}
                    placeholder={t('auth.confirm_placeholder')}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(v => !v)}
                    className="absolute inset-y-0 right-0 flex items-center justify-center w-10 p-2 z-10 text-slate-400 hover:text-slate-600 dark:hover:text-neutral-300 focus:outline-none"
                    tabIndex={-1}
                    aria-label={showConfirm ? 'Hide password' : 'Show password'}
                  >
                    {showConfirm
                      ? <EyeOff className="w-4 h-4" strokeWidth={1.5} />
                      : <Eye className="w-4 h-4" strokeWidth={1.5} />}
                  </button>
                </div>
                {confirmPwd.length > 0 && !pwdMatch && (
                  <p className="text-[11px] text-red-500 font-body mt-1">{t('auth.pwd_mismatch')}</p>
                )}
              </div>
            )}

            {/* CGV & Privacy checkbox — register only */}
            {isRegister && (
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="accept-terms"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 dark:border-white/20 text-blue-600 focus:ring-blue-600"
                  required
                  data-testid="register-accept-terms"
                />
                <label htmlFor="accept-terms" className="text-slate-600 dark:text-neutral-400 text-xs font-body leading-relaxed">
                  {language === 'fr' ? (
                    <>J'ai lu et j'accepte les <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-500 underline">Conditions Générales de Vente</Link> et la <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-500 underline">Politique de confidentialité</Link>.</>
                  ) : (
                    <>I have read and accept the <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-500 underline">Terms of Service</Link> and <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-500 underline">Privacy Policy</Link>.</>
                  )}
                </label>
              </div>
            )}

            {/* Generated password toast */}
            {genToast && (
              <div className="flex items-start gap-2 p-3 bg-green-600/10 border border-green-600/30 rounded-sm">
                <Copy className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <p className="text-green-400 text-[11px] font-body leading-relaxed">{genToast}</p>
              </div>
            )}

            {/* Messages */}
            {successMsg && (
              <p className="text-green-500 text-xs font-body" data-testid="auth-success">{successMsg}</p>
            )}
            {error && (
              <p className="text-red-500 text-xs font-body" data-testid="auth-error">{error}</p>
            )}

            {/* Resend verification — shown after login attempt with unverified account */}
            {showVerifyPrompt && !isRegister && (
              <div className="space-y-2">
                {resendSuccess ? (
                  <p className="text-green-500 text-xs font-body">{t('auth.resend_success')}</p>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                    className="flex items-center gap-1.5 text-blue-400 hover:text-blue-300 text-xs font-body underline disabled:opacity-50 transition-colors"
                    data-testid="resend-verification-btn"
                  >
                    <RefreshCw className={`w-3 h-3 ${resendLoading ? 'animate-spin' : ''}`} strokeWidth={2} />
                    {resendLoading ? t('auth.resend_loading') : t('auth.resend_verification')}
                  </button>
                )}
              </div>
            )}

            {/* Submit button */}
            <Button
              type="submit"
              disabled={isRegister ? registerDisabled : loginDisabled}
              className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-body tracking-widest text-xs rounded-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              data-testid="auth-submit-button"
            >
              {loading && isRegister
                ? SECURITY_THEATER_STEPS[theaterStep]
                : loading
                ? t('auth.connecting')
                : isRegister
                ? t('auth.register')
                : t('auth.login')}
            </Button>

            {/* Zero-knowledge micro-copy */}
            <div className="flex items-center justify-center gap-1.5 pt-1">
              <Lock className="w-3 h-3 text-neutral-500" strokeWidth={1.5} />
              <span className="text-neutral-500 dark:text-neutral-600 text-[11px] font-body">
                {t('auth.zero_knowledge')}
              </span>
            </div>
          </form>

          <p className="mt-5 text-center text-slate-600 dark:text-neutral-500 text-sm font-body">
            {isRegister ? t('auth.has_account') : t('auth.no_account')}{' '}
            <button
              onClick={handleToggleMode}
              className="text-blue-600 hover:text-blue-500 dark:hover:text-blue-400"
              data-testid="toggle-auth-mode"
            >
              {isRegister ? t('auth.login') : t('auth.register')}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
